import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Onboarding, OnboardingPhase, OnboardingStatus } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE, ONBOARDING_PHASES, ONBOARDING_STATUSES } from '@catalyst-psa/shared';

import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/date.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import { savedString, savedStringArray, useFilterDefaults } from '../filters/useFilterDefaults.js';
import { OPPORTUNITY_STATUS_LABELS, OPPORTUNITY_STATUS_TONES } from '../opportunities/status.js';
import { useOnboardings } from './api.js';
import { OnboardingDetailModal } from './OnboardingDetailModal.js';
import { useTrackRecent } from '../quick-access/useTrackRecent.js';
import {
  ONBOARDING_PHASE_LABELS,
  ONBOARDING_PHASE_TONES,
  ONBOARDING_STATUS_LABELS,
  ONBOARDING_STATUS_TONES,
} from './status.js';

/** Delivery-phase filter values: the real phases plus 'none' (no phase set). */
type PhaseFilterValue = OnboardingPhase | 'none';

const PHASE_FILTER_OPTIONS: readonly PhaseFilterValue[] = [...ONBOARDING_PHASES, 'none'];

const PHASE_FILTER_LABELS: Record<PhaseFilterValue, string> = {
  ...ONBOARDING_PHASE_LABELS,
  none: 'Not set',
};

/** Exception filter: unset = all, otherwise flagged / not flagged. */
type ExceptionFilterValue = '' | 'true' | 'false';

interface QuickFilter {
  label: string;
  statuses: OnboardingStatus[];
  phases: PhaseFilterValue[];
}

const QUICK_FILTERS: readonly QuickFilter[] = [
  { label: 'New Orders', statuses: ['in_progress'], phases: ['none'] },
  { label: 'Setup', statuses: ['in_progress', 'complete'], phases: ['systems_setup'] },
  { label: 'Implementing', statuses: ['complete'], phases: ['in_implementation'] },
  { label: 'Ready to Bill', statuses: ['complete'], phases: ['ready_to_bill'] },
];

function sameSelection(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

/**
 * Compact multi-select filter: a dropdown button revealing a checkbox list. Empty
 * selection = no filtering (the "All …" label).
 */
function MultiFilterSelect<T extends string>({
  options,
  labels,
  selected,
  allLabel,
  onChange,
}: {
  options: readonly T[];
  labels: Record<T, string>;
  selected: T[];
  allLabel: string;
  onChange: (next: T[]) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length <= 2
        ? selected.map((value) => labels[value]).join(', ')
        : `${selected.length} selected`;

  const toggle = (value: T, checked: boolean): void => {
    onChange(checked ? [...selected, value] : selected.filter((v) => v !== value));
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex min-w-[12rem] items-center justify-between gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      >
        <span className="truncate">{summary}</span>
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 w-56 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <button
            type="button"
            onClick={() => onChange([])}
            className="mb-1 w-full rounded-md border-b border-slate-100 px-2 py-1.5 text-left text-xs font-medium text-slate-500 hover:bg-slate-50"
          >
            Clear ({allLabel.toLowerCase()})
          </button>
          {options.map((value) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={selected.includes(value)}
                onChange={(e) => toggle(value, e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span>{labels[value]}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ProgressCell({ onboarding }: { onboarding: Onboarding }): JSX.Element {
  const total = onboarding.totalSteps || 1;
  const completedPct = (onboarding.completedCount / total) * 100;
  const skippedPct = (onboarding.skippedCount / total) * 100;
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-2 w-28 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full bg-emerald-500" style={{ width: `${completedPct}%` }} />
        <div className="h-full bg-sky-400" style={{ width: `${skippedPct}%` }} />
      </div>
      <span className="text-xs tabular-nums text-slate-500">
        {onboarding.completedCount + onboarding.skippedCount}/{onboarding.totalSteps}
      </span>
    </div>
  );
}

export function OnboardingPage(): JSX.Element {
  const navigate = useNavigate();
  const rowLink = useRowLink();
  const { id: routeId } = useParams<{ id?: string }>();
  const [page, setPage] = useState(1);
  // Empty = all statuses (the default).
  const [statuses, setStatuses] = useState<OnboardingStatus[]>([]);
  // Default delivery-phase view: In Implementation + Ready to Bill.
  const [phases, setPhases] = useState<PhaseFilterValue[]>(['in_implementation', 'ready_to_bill']);
  const [customerId, setCustomerId] = useState('');
  const [exception, setException] = useState<ExceptionFilterValue>('');
  const [openId, setOpenId] = useState<string | null>(null);

  const filterDefaults = useFilterDefaults('onboarding', (saved) => {
    const savedStatuses = savedStringArray(saved, 'statuses');
    if (savedStatuses !== null) setStatuses(savedStatuses as OnboardingStatus[]);
    const savedPhases = savedStringArray(saved, 'phases');
    if (savedPhases !== null) setPhases(savedPhases as PhaseFilterValue[]);
    const savedException = savedString(saved, 'exception');
    if (savedException !== null) setException(savedException as ExceptionFilterValue);
  });

  // Deep-link: /sales/onboarding/:id opens that onboarding's modal.
  useEffect(() => {
    if (routeId) setOpenId(routeId);
  }, [routeId]);

  const closeDetail = (): void => {
    setOpenId(null);
    if (routeId) navigate('/sales/onboarding', { replace: true });
  };

  const query = useOnboardings({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    // A customer search shows ALL of that customer's records — the other filters are
    // intentionally bypassed while a customer is selected.
    statuses: !customerId && statuses.length > 0 ? statuses : undefined,
    phases: !customerId && phases.length > 0 ? phases : undefined,
    customerId: customerId || undefined,
    isException: customerId || exception === '' ? undefined : exception === 'true',
    sortBy: 'createdAt',
    sortDir: 'desc',
  });

  const onboardings = query.data?.data ?? [];
  const pageInfo = query.data?.page;
  const hasFilters = Boolean(
    statuses.length > 0 || phases.length > 0 || customerId || exception !== '',
  );

  const openOnboarding = openId ? (onboardings.find((o) => o.id === openId) ?? null) : null;
  useTrackRecent(
    openOnboarding
      ? {
          entity: 'onboarding',
          id: openOnboarding.id,
          label: openOnboarding.customerName || openOnboarding.opportunityName || 'Onboarding',
          path: `/sales/onboarding/${openOnboarding.id}`,
        }
      : null,
  );

  return (
    <div>
      <PageHeader
        title="Onboarding"
        description="Customer onboarding checklists. A record is created automatically when an opportunity is set to Order Approved."
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {QUICK_FILTERS.map((filter) => {
          const active =
            sameSelection(statuses, filter.statuses) && sameSelection(phases, filter.phases);
          return (
            <button
              key={filter.label}
              type="button"
              onClick={() => {
                setStatuses(filter.statuses);
                setPhases(filter.phases);
                setPage(1);
              }}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium ${
                active
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {filter.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => {
            setStatuses([]);
            setPhases([]);
            setCustomerId('');
            setException('');
            setPage(1);
          }}
          className={`rounded-full border px-3 py-1.5 text-sm font-medium ${
            !hasFilters
              ? 'border-brand-600 bg-brand-600 text-white'
              : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          Show All
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <label className="mb-1 block text-xs font-medium text-slate-500">Customer</label>
          <CustomerSelectField
            value={customerId}
            onChange={(id) => {
              setCustomerId(id);
              setPage(1);
            }}
            placeholder="All customers"
            allowClear
          />
          {customerId ? (
            <p className="mt-1 text-xs text-amber-600">
              Showing all records for this customer — other filters are ignored.
            </p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Status</label>
          <MultiFilterSelect
            options={ONBOARDING_STATUSES}
            labels={ONBOARDING_STATUS_LABELS}
            selected={statuses}
            allLabel="All statuses"
            onChange={(next) => {
              setStatuses(next);
              setPage(1);
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Delivery phase</label>
          <MultiFilterSelect
            options={PHASE_FILTER_OPTIONS}
            labels={PHASE_FILTER_LABELS}
            selected={phases}
            allLabel="All phases"
            onChange={(next) => {
              setPhases(next);
              setPage(1);
            }}
          />
        </div>
        <div>
          <label
            htmlFor="onboarding-exception-filter"
            className="mb-1 block text-xs font-medium text-slate-500"
          >
            Exception
          </label>
          <select
            id="onboarding-exception-filter"
            value={exception}
            onChange={(e) => {
              setException(e.target.value as ExceptionFilterValue);
              setPage(1);
            }}
            className="min-w-[12rem] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All records</option>
            <option value="true">Exceptions only</option>
            <option value="false">Not exceptions</option>
          </select>
        </div>
        <FilterDefaultsButtons
          handle={filterDefaults}
          onSave={() => filterDefaults.save({ statuses, phases, exception })}
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Customer</th>
              <th className="px-4 py-2 font-medium">Opportunity</th>
              <th className="px-4 py-2 font-medium">Opportunity status</th>
              <th className="px-4 py-2 font-medium">Progress</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Delivery phase</th>
              <th className="px-4 py-2 font-medium">Exception</th>
              <th className="px-4 py-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={!query.isLoading && onboardings.length === 0}
              emptyTitle={
                hasFilters ? 'No matching onboarding records' : 'No onboarding records yet'
              }
              emptyDescription={
                hasFilters
                  ? 'Try clearing the filters.'
                  : 'Set an opportunity to Order Approved to start onboarding a customer.'
              }
              colSpan={8}
            >
              {onboardings.map((onboarding) => (
                <tr
                  key={onboarding.id}
                  {...rowLink(`/sales/onboarding/${onboarding.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-4 py-2 font-medium text-slate-800">
                    {onboarding.customerName}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{onboarding.opportunityName}</td>
                  <td className="px-4 py-2">
                    <Badge tone={OPPORTUNITY_STATUS_TONES[onboarding.opportunityStatus]}>
                      {OPPORTUNITY_STATUS_LABELS[onboarding.opportunityStatus]}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    <ProgressCell onboarding={onboarding} />
                  </td>
                  <td className="px-4 py-2">
                    <Badge tone={ONBOARDING_STATUS_TONES[onboarding.status]}>
                      {ONBOARDING_STATUS_LABELS[onboarding.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    {onboarding.phase ? (
                      <Badge tone={ONBOARDING_PHASE_TONES[onboarding.phase]}>
                        {ONBOARDING_PHASE_LABELS[onboarding.phase]}
                      </Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {onboarding.isException ? (
                      <Badge tone="red">Exception</Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-500">{formatDate(onboarding.createdAt)}</td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
        {pageInfo && onboardings.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <OnboardingDetailModal onboardingId={openId} onClose={closeDetail} />
    </div>
  );
}
